import { useEffect, useState } from "react";
import L from "leaflet";
import { MapContainer, ImageOverlay, Marker, Tooltip, Polygon, Polyline, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import { api } from "@/lib/api";
import { catColor } from "@/lib/lore";
import { renderGridUrl } from "@/lib/biomes";
import { TravelPathLayer } from "@/components/TravelPathLayer";
import { EventPulseLayer } from "@/components/PlaybackLayers";

const pinIcon = (color) => L.divIcon({ className: "lf-pin-wrap", html: `<div class="lf-pin" style="background:${color}"></div>`, iconSize: [26, 26], iconAnchor: [13, 30] });

function ClickCatcher({ onClick, draft, onDraft }) {
  const map = useMapEvents({
    click: (e) => {
      if (!draft) return onClick?.(e.latlng);
      if (draft.length >= 3) {
        const a = map.latLngToContainerPoint(draft[0]), b = map.latLngToContainerPoint(e.latlng);
        if (a.distanceTo(b) < 14) return onDraft(draft, true);
      }
      onDraft([...draft, e.latlng], false);
    },
  });
  return null;
}

const handleIcon = (on) => L.divIcon({ className: "", html: `<div class="lf-vertex${on ? " lf-vertex-on" : ""}"></div>`, iconSize: [22, 22], iconAnchor: [11, 11] });
const midIcon = L.divIcon({ className: "", html: '<div class="lf-vertex-mid">+</div>', iconSize: [18, 18], iconAnchor: [9, 9] });

const ShapeEditor = ({ edit, onEdit }) => {
  const pts = edit.points;
  const move = (i) => (e) => { const { lat, lng } = e.target.getLatLng(); onEdit({ ...edit, points: pts.map((p, j) => (j === i ? [lng, lat] : p)), selected: i }); };
  const insert = (i) => { const a = pts[i], b = pts[(i + 1) % pts.length]; const next = [...pts]; next.splice(i + 1, 0, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]); onEdit({ ...edit, points: next, selected: i + 1 }); };
  const remove = (i) => pts.length > 3 && onEdit({ ...edit, points: pts.filter((_, j) => j !== i), selected: null });
  return (
    <>
      <Polygon positions={pts.map(([x, y]) => [y, x])} interactive={false} pathOptions={{ color: "#F59E0B", weight: 2, fillColor: edit.color, fillOpacity: 0.35 }} />
      {pts.map((p, i) => {
        const q = pts[(i + 1) % pts.length];
        return <Marker key={`m${i}-${pts.length}`} position={[(p[1] + q[1]) / 2, (p[0] + q[0]) / 2]} icon={midIcon}
          eventHandlers={{ click: () => insert(i), add: (e) => e.target.getElement()?.setAttribute("data-testid", `shape-mid-${i}`) }} />;
      })}
      {pts.map((p, i) => (
        <Marker key={`v${i}-${pts.length}`} position={[p[1], p[0]]} draggable icon={handleIcon(edit.selected === i)}
          eventHandlers={{ drag: move(i), dragend: move(i), click: () => onEdit({ ...edit, selected: i }), dblclick: () => remove(i),
            add: (e) => e.target.getElement()?.setAttribute("data-testid", `shape-vertex-${i}`) }} />
      ))}
    </>
  );
};

const FlyTo = ({ focus }) => {
  const map = useMap();
  useEffect(() => { if (focus) map.flyTo([focus.y, focus.x], Math.max(map.getZoom(), 0), { duration: 0.6 }); }, [focus, map]);
  return null;
};

const RegionLayer = ({ regions, labels, onRegionClick, drawing }) => regions.map((r) => (
  <Polygon key={`${r.id}-${r.activeStage?.id || "base"}`} positions={r.points.map(([x, y]) => [y, x])} interactive={!drawing}
    pathOptions={{ color: r.color, weight: 1.5, dashArray: "6 3", fillColor: r.color, fillOpacity: r.opacity ?? 0.3, className: "lf-region" }}
    eventHandlers={{ click: () => onRegionClick(r), add: (e) => e.target.getElement()?.setAttribute("data-testid", `map-region-${r.id}`) }}>
    <Tooltip key={labels ? "perm" : "hover"} permanent={labels} direction="center" className="lf-tip">{r.name}{r.entity && r.entity.name !== r.name ? ` · ${r.entity.name}` : ""}</Tooltip>
  </Polygon>
));

const DraftLayer = ({ draft }) => draft?.length ? (
  <>
    <Polyline positions={draft} pathOptions={{ color: "#F59E0B", dashArray: "4 4", weight: 2 }} />
    {draft.map((p, i) => <CircleMarker key={i} center={p} radius={i === 0 ? 7 : 4} pathOptions={{ color: "#F59E0B", fillColor: i === 0 ? "#F59E0B" : "#0B0D10", fillOpacity: 1 }} />)}
  </>
) : null;

function SizeWatch({ bounds, refit }) {
  const m = useMap();
  useEffect(() => {
    let w = m.getContainer().offsetWidth, h = m.getContainer().offsetHeight, t = null;
    const ro = new ResizeObserver(() => {
      clearTimeout(t);
      t = setTimeout(() => {
        const c = m.getContainer();
        if (Math.abs(c.offsetWidth - w) < 2 && Math.abs(c.offsetHeight - h) < 2) return;
        w = c.offsetWidth; h = c.offsetHeight;
        m.invalidateSize({ animate: false });
        if (refit) m.fitBounds(bounds, { animate: false });
      }, 120);
    });
    ro.observe(m.getContainer());
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [m, refit]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function useMapImage(map) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (map.kind === "painted") {
      setUrl(renderGridUrl(map.grid, map.cols, map.rows));
      return;
    }
    let obj;
    setUrl(null);
    api.get(`/maps/${map.id}/image`, { responseType: "blob" }).then((r) => {
      obj = URL.createObjectURL(r.data);
      setUrl(obj);
    }).catch(() => setUrl("error"));
    return () => obj && URL.revokeObjectURL(obj);
  }, [map.id, map.kind, map.grid, map.cols, map.rows]);
  return url;
}

export const MapCanvas = ({ map, pins, onMapClick, onPinClick, placing, regions = [], layers = { pins: true, regions: true, labels: false }, onRegionClick, draft, onDraft, edit, onEdit, paths = [], pathT = null, focus, smooth = false, pulses = null, refit = false }) => {
  const url = useMapImage(map);
  const bounds = [[0, 0], [map.height, map.width]];
  if (!url) return <div className="h-full flex items-center justify-center text-[#A8A090] font-display text-2xl" data-testid="map-loading">Unrolling the map…</div>;
  if (url === "error") return <div className="h-full flex items-center justify-center text-red-400">Could not load map image.</div>;
  return (
    <div className="h-full w-full" data-testid="map-canvas">
    <MapContainer key={map.id} crs={L.CRS.Simple} bounds={bounds} maxBounds={[[-map.height * 0.25, -map.width * 0.25], [map.height * 1.25, map.width * 1.25]]}
      minZoom={-4} maxZoom={3} zoomSnap={0.25} className={`h-full w-full ${placing || draft ? "cursor-crosshair" : ""}`} attributionControl={false}>
      <ImageOverlay url={url} bounds={bounds} />
      <SizeWatch bounds={bounds} refit={refit} />
      <ClickCatcher onClick={edit ? null : onMapClick} draft={draft} onDraft={onDraft} />
      {layers.regions && <RegionLayer regions={edit ? regions.filter((r) => r.id !== edit.id) : regions} labels={layers.labels} onRegionClick={onRegionClick} drawing={!!draft || !!edit} />}
      {edit && <ShapeEditor edit={edit} onEdit={onEdit} />}
      <TravelPathLayer paths={paths} t={pathT} smooth={smooth} />
      {pulses && layers.pins && <EventPulseLayer {...pulses} />}
      <FlyTo focus={focus} />
      <DraftLayer draft={draft} />
      {layers.pins && pins.map((p) => (
        <Marker key={p.id} position={[p.y_coord, p.x_coord]} icon={pinIcon(catColor(p.entity?.category))}
          eventHandlers={{ click: () => onPinClick(p), add: (e) => e.target.getElement()?.setAttribute("data-testid", `map-pin-${p.id}`) }}>
          <Tooltip key={layers.labels ? "perm" : "hover"} permanent={layers.labels} direction="top" offset={[0, -28]} className="lf-tip">{p.label || p.entity?.name || "Unnamed pin"}</Tooltip>
        </Marker>
      ))}
    </MapContainer>
    </div>
  );
};
